import * as React from 'react'; 
import MyTypes from 'MyTypes';
import { bindActionCreators, Dispatch } from 'redux';
import { connect, ConnectedProps } from 'react-redux';
import Chart from 'react-apexcharts'; 
import * as models from '../../../models'; 
import services from '../../../services';
import _ from 'lodash'; 
import { claimsActions } from '../../../store/claims';

interface Props extends PropsFromRedux {    
    claimsByCarrier: models.ClaimsByCarrier[]; 
    fieldAdjusterId?: number;
    /** Explicit pixel height from parent (metrics column). Avoids ResizeObserver growth loops. */
    height?: number;
}

interface State {
    series: number[];  
    options: any;
}

class ClaimsByCarrierPie extends React.PureComponent<Props, State> {
    public state: State = {
        options: {},
        series: [1],
    }

    public componentDidMount = () => {
        this.loadDataToChart();
    }
    
    public componentDidUpdate(prevProps: Readonly<Props>): void {
        if (!_.isEqual(prevProps.claimsByCarrier, this.props.claimsByCarrier)) {
            this.loadDataToChart();
        }
    }

    public handleOpenClaimsByCarrierClick = (companyName: string) => {
        if (!companyName || companyName === 'No Open Claims') {
            return;
        }

        const claimsRequest: models.ClaimsRequest = {
            ...models.defaultClaimsRequest, 
            insCompany: companyName,
            fieldAdjusterId: this.props.fieldAdjusterId,
            status: "Open,Reopened"
        }
        this.props.requestClaims(claimsRequest);
        services.router.goto(services.router.url.claimList); 
    }

    buildChartConfig = (carriers: string[], claims: number[], isEmpty: boolean) => ({
        title: {
            text: "Open Claims By Carrier",
            align: 'center',                    
            fontSize: '32px', 
        },
        labels: carriers,
        colors: isEmpty ? ['#dee2e6'] : undefined,
        tooltip: { enabled: !isEmpty }, 
        legend: {
            show: !isEmpty, 
            position: 'left',
            fontSize: '16px', 
            fontWeight: 400,
            floating: true, 
        }, 
        chart: {      
            type: 'donut',
            parentHeightOffset: 0,
            zoom: {
                enabled: false,
            },
            events: {
                dataPointSelection: (_event: any, _chartContext: any, config: any) => {
                    if (isEmpty) {
                        return;
                    }
                    this.handleOpenClaimsByCarrierClick(carriers[config.dataPointIndex]);
                }
            }
        },       
        plotOptions: {
            pie: {                        
                customScale: 0.9,
                expandOnClick: !isEmpty,
                donut: {
                    size: '65%',
                    background: 'transparent',
                    labels: {
                        show: true,      
                        name: {
                            show: true, 
                            fontSize: '16px', 
                            fontWeight: 600,
                            color: undefined
                        }, 
                        value: {
                            fontSize: '32px', 
                            fontWeight: 400,
                            offsetY: 16,
                            formatter: (val: string) => isEmpty ? '0' : val,
                        },                                                                                                                         
                        total: {
                            show: true,
                            showAlways: true, 
                            label: 'Total',
                            fontSize: '24px',
                            fontWeight: 600,    
                            color: '#373d3f',                               
                            formatter: function (w: any) {
                                if (isEmpty) {
                                    return '0';
                                }
                                return w.globals.seriesTotals.reduce((a: number, b: number) => a + b, 0);
                            }
                        }                                               
                    }
                }       
            },                                                                             
        }
    })

    loadDataToChart = () => {
        const { claimsByCarrier } = this.props;
        const isEmpty = !claimsByCarrier || claimsByCarrier.length === 0;
        const carriers = isEmpty
            ? ['No Open Claims']
            : claimsByCarrier.map(x => x.carrier);
        // Apex needs a non-zero slice to draw the ring; total label still shows 0 when empty.
        const claims = isEmpty
            ? [1]
            : claimsByCarrier.map(x => x.claims);

        this.setState({
            options: this.buildChartConfig(carriers, claims, isEmpty),
            series: claims,
        }); 
    }

    render() {
    const chartHeight = Math.max(420, this.props.height || 420);

        return (
            <div className="adjuster-dashboard-pie-host">
                <Chart 
                    options={this.state.options} 
                    series={this.state.series} 
                    type="donut" 
                    width="100%"
                    height={chartHeight}
                />
            </div>
        );
    }
}
   
const mapStateToProps = (state: MyTypes.RootState) => ({
});

const mapDispatchToProps = (dispatch: Dispatch) => 
    bindActionCreators(
        {
            requestClaims: claimsActions.requestClaims,
        },
        dispatch
    );

const connector = connect(mapStateToProps, mapDispatchToProps); 
type PropsFromRedux = ConnectedProps<typeof connector>
    
export default connector(ClaimsByCarrierPie);
